/**
 * A STORE-only zip (no compression) — ADR-033 phase 7's fallback container.
 *
 * Where the browser has no `showDirectoryPicker` (Firefox, Safari), the
 * package still has to leave as ONE thing through the save picker; a zip of
 * the same folder is that thing. Stored, not deflated: the GLB is already
 * compact and the PNG is already compressed, and a dependency for a
 * container format this small would be a heavier thing than the format.
 * PKZIP local headers + central directory + end record, version 2.0, UTF-8
 * names, DOS time from the given date.
 *
 * Depends on: nothing. Depended on by: package-folder, tests.
 */

export interface ZipEntry {
  /** Forward-slash path inside the archive, e.g. `hero/amari.png`. */
  readonly path: string;
  readonly bytes: Uint8Array;
}

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

export function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) c = CRC_TABLE[(c ^ bytes[i]!) & 0xff]! ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

function dosDateTime(d: Date): { time: number; date: number } {
  const year = Math.max(1980, d.getFullYear());
  return {
    time: (d.getHours() << 11) | (d.getMinutes() << 5) | Math.floor(d.getSeconds() / 2),
    date: ((year - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate(),
  };
}

export function storeZip(entries: readonly ZipEntry[], when: Date = new Date()): Uint8Array {
  const enc = new TextEncoder();
  const { time, date } = dosDateTime(when);
  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;
  for (const e of entries) {
    if (e.path === '' || e.path.startsWith('/') || e.path.includes('\\') || e.path.split('/').includes('..')) {
      throw new Error(`zip: refusing entry path "${e.path}"`);
    }
    const name = enc.encode(e.path);
    const crc = crc32(e.bytes);
    const local = new Uint8Array(30 + name.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, 0x04034b50, true);
    lv.setUint16(4, 20, true); // version needed
    lv.setUint16(6, 0x0800, true); // UTF-8 names
    lv.setUint16(8, 0, true); // stored
    lv.setUint16(10, time, true);
    lv.setUint16(12, date, true);
    lv.setUint32(14, crc, true);
    lv.setUint32(18, e.bytes.length, true);
    lv.setUint32(22, e.bytes.length, true);
    lv.setUint16(26, name.length, true);
    lv.setUint16(28, 0, true);
    local.set(name, 30);
    const central = new Uint8Array(46 + name.length);
    const cv = new DataView(central.buffer);
    cv.setUint32(0, 0x02014b50, true);
    cv.setUint16(4, 20, true); // version made by
    cv.setUint16(6, 20, true); // version needed
    cv.setUint16(8, 0x0800, true);
    cv.setUint16(10, 0, true);
    cv.setUint16(12, time, true);
    cv.setUint16(14, date, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, e.bytes.length, true);
    cv.setUint32(24, e.bytes.length, true);
    cv.setUint16(28, name.length, true);
    cv.setUint16(30, 0, true); // extra
    cv.setUint16(32, 0, true); // comment
    cv.setUint16(34, 0, true); // disk
    cv.setUint16(36, 0, true); // internal attrs
    cv.setUint32(38, 0, true); // external attrs
    cv.setUint32(42, offset, true);
    central.set(name, 46);
    locals.push(local, e.bytes);
    centrals.push(central);
    offset += local.length + e.bytes.length;
  }
  const centralSize = centrals.reduce((n, c) => n + c.length, 0);
  const end = new Uint8Array(22);
  const ev = new DataView(end.buffer);
  ev.setUint32(0, 0x06054b50, true);
  ev.setUint16(4, 0, true);
  ev.setUint16(6, 0, true);
  ev.setUint16(8, entries.length, true);
  ev.setUint16(10, entries.length, true);
  ev.setUint32(12, centralSize, true);
  ev.setUint32(16, offset, true);
  ev.setUint16(20, 0, true);
  const total = offset + centralSize + end.length;
  const out = new Uint8Array(total);
  let at = 0;
  for (const part of [...locals, ...centrals, end]) {
    out.set(part, at);
    at += part.length;
  }
  return out;
}

/** Read a stored zip's directory back — the test's oracle and a debugging aid; not a general unzipper. */
export function listStoreZip(zip: Uint8Array): { path: string; size: number; crc: number; offset: number }[] {
  const v = new DataView(zip.buffer, zip.byteOffset, zip.byteLength);
  const endAt = zip.length - 22;
  if (v.getUint32(endAt, true) !== 0x06054b50) throw new Error('zip: no end record');
  const count = v.getUint16(endAt + 10, true);
  let at = v.getUint32(endAt + 16, true);
  const dec = new TextDecoder();
  const out: { path: string; size: number; crc: number; offset: number }[] = [];
  for (let i = 0; i < count; i += 1) {
    if (v.getUint32(at, true) !== 0x02014b50) throw new Error(`zip: bad central header at ${at}`);
    const nameLen = v.getUint16(at + 28, true);
    out.push({ crc: v.getUint32(at + 16, true), size: v.getUint32(at + 24, true), offset: v.getUint32(at + 42, true), path: dec.decode(zip.subarray(at + 46, at + 46 + nameLen)) });
    at += 46 + nameLen + v.getUint16(at + 30, true) + v.getUint16(at + 32, true);
  }
  return out;
}
